import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import DashboardLayout from '@/shared/layout/DashboardLayout';
import PageState from '@/shared/components/PageState';
import ResultLoadingState from '@/shared/components/ResultLoadingState';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {apiClient} from '@/shared/services';
import SingleResultContent from './SingleResultContent';
import type {Test} from '@/shared/types/api';

const ProviderSingleResult: React.FC = () => {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const location = useLocation();
    const returnTo = (location.state as {returnTo?: string} | null)?.returnTo?.toString() || '/provider';
    const {user, token} = useAuth();

    const [serverOrder, setServerOrder] = useState<Test | null>(null);
    const [serverOrderLoading, setServerOrderLoading] = useState(false);
    const [serverOrderError, setServerOrderError] = useState<string | null>(null);

    // Fetch the order from the API.  `silent` skips the full-page loading
    // state so refetches triggered by a clinical-flag change re-render
    // results in place without flashing the loading skeleton.
    const fetchOrder = useCallback(
        async ({silent}: {silent: boolean} = {silent: false}) => {
            if (!id) return;
            if (!silent) {
                setServerOrderError(null);
                setServerOrderLoading(true);
            }
            try {
                const order = await apiClient.get<Test>(`/api/order/${id}`);
                setServerOrder(order);
            } catch (error: unknown) {
                const message =
                    error instanceof Error ? error.message : 'Unable to load this order. You may not have access.';
                if (!silent) {
                    setServerOrder(null);
                    setServerOrderError(message);
                }
            } finally {
                if (!silent) setServerOrderLoading(false);
            }
        },
        [id]
    );

    useEffect(() => {
        if (!user?.isLoggedIn) return;
        if (!id) return;
        void fetchOrder({silent: false});
    }, [id, user?.isLoggedIn, fetchOrder]);

    const refetchOrder = useCallback(() => {
        void fetchOrder({silent: true});
    }, [fetchOrder]);

    const providerNeedsFetch = !serverOrder && !serverOrderError;
    if (serverOrderLoading || providerNeedsFetch) {
        return (
            <DashboardLayout>
                <ResultLoadingState />
            </DashboardLayout>
        );
    }

    if (serverOrderError) {
        return (
            <DashboardLayout>
                <PageState
                    tone="error"
                    title="Unable to Load Order"
                    description={serverOrderError}
                    primaryAction={{label: 'Back to Provider Console', onClick: () => navigate(returnTo)}}
                />
            </DashboardLayout>
        );
    }

    if (!serverOrder) {
        return (
            <DashboardLayout>
                <PageState
                    title="Result Not Found"
                    description="The requested test result could not be found."
                    primaryAction={{label: 'Back to Provider Console', onClick: () => navigate(returnTo)}}
                />
            </DashboardLayout>
        );
    }

    const downloadToken = token || user?.token;
    return (
        <SingleResultContent
            result={serverOrder}
            realResult={serverOrder}
            token={downloadToken}
            backTo={returnTo}
            backLabel="Back to Provider Console"
            onOrderRefetch={refetchOrder}
        />
    );
};

export default ProviderSingleResult;
